import React, { useEffect, useRef } from 'react';
import { VaTelephoneInput } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import vaTelephoneInputFieldMapping from 'platform/forms-system/src/js/web-component-fields/vaTelephoneInputFieldMapping';

const US_COUNTRY_CODE = 'US';
const US_CALLING_CODE = 1;

const lockCountryComboBox = telephoneInput => {
  const comboBox = telephoneInput?.shadowRoot?.querySelector('va-combo-box');
  if (comboBox) {
    comboBox.disabled = true;
    comboBox.setAttribute('disabled', 'true');
  }
};

const observeLockedCountryComboBox = telephoneInput => {
  const shadowRoot = telephoneInput?.shadowRoot;
  if (!shadowRoot) {
    return undefined;
  }

  lockCountryComboBox(telephoneInput);

  const observer = new MutationObserver(() => {
    lockCountryComboBox(telephoneInput);
  });

  observer.observe(shadowRoot, { childList: true, subtree: true });

  return () => observer.disconnect();
};

const normalizeLockedCountryPayload = (payload, lockedCountry) => {
  if (!lockedCountry || payload.countryCode === lockedCountry) {
    return payload;
  }

  return {
    ...payload,
    countryCode: lockedCountry,
    callingCode:
      lockedCountry === US_COUNTRY_CODE ? US_CALLING_CODE : payload.callingCode,
  };
};

/**
 * Wrapper around the default telephone input field so we can disable the
 * component's internal validation messages. This lets the form system decide
 * when to surface errors (e.g. only after an invalid entry).
 */
export default function TelephoneFieldNoInternalErrors(props) {
  const telephoneRef = useRef(null);
  const mappedProps = vaTelephoneInputFieldMapping(props);
  const { initialCountry, lockCountry, noCountry } = props.uiOptions || {};
  const lockedCountry = lockCountry || (noCountry ? US_COUNTRY_CODE : null);
  const country =
    lockedCountry ||
    props.childrenProps?.formData?.countryCode ||
    initialCountry ||
    mappedProps.country;

  useEffect(() => {
    if (!lockCountry || !telephoneRef.current) {
      return undefined;
    }

    const stopObserving = observeLockedCountryComboBox(telephoneRef.current);
    const timeoutId = window.setTimeout(() => {
      lockCountryComboBox(telephoneRef.current);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      stopObserving?.();
    };
  }, [lockCountry]);

  const onVaContact = (event, value) => {
    const payload = value || event?.detail || {};
    const normalizedPayload = normalizeLockedCountryPayload(
      payload,
      lockedCountry,
    );

    mappedProps.onVaContact(event, {
      ...normalizedPayload,
      contactRaw: normalizedPayload.contactRaw ?? normalizedPayload.contact,
    });
  };

  return (
    <VaTelephoneInput
      ref={telephoneRef}
      {...mappedProps}
      country={country}
      noCountry={Boolean(noCountry && !lockCountry)}
      onVaContact={onVaContact}
      showInternalErrors={false}
    />
  );
}
